import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Zoom and scroll for the timeline, in pixels-per-second.
 *
 * The old timeline laid clips out as a percentage of the total duration, which
 * meant the scale changed every time the edit got longer: add a clip and every
 * other clip silently shrank. Worse, a 2-second trim on a 5-minute edit was
 * sub-pixel — unclickable. A fixed pixels-per-second scale with a scrollable
 * viewport is what makes fine work possible, and it is why zoom exists.
 */

const MIN_PPS = 2;
const MAX_PPS = 400;
const DEFAULT_PPS = 40;

export function useZoomPan({ totalDuration, viewportRef }) {
    const [pixelsPerSecond, setPixelsPerSecond] = useState(DEFAULT_PPS);
    const [metrics, setMetrics] = useState({ width: 0, labelWidth: 0, handleWidth: 0 });
    const pendingScrollRef = useRef(null);

    /* The label gutter sits inside the same scroll container as the tracks, so
       the width available *for time* is the viewport minus the gutter. Measuring
       the gutter from the CSS variable keeps this in step with the narrow-screen
       override instead of hardcoding the number twice. */
    useLayoutEffect(() => {
        const element = viewportRef.current;
        if (!element) return undefined;

        const measure = (width) => {
            const style = getComputedStyle(element);
            const labelWidth = parseFloat(style.getPropertyValue('--nle-label-width')) || 0;
            /* Read rather than duplicated in JS: the coarse-pointer override
               widens the trim handles, and the width a clip must exceed before
               it can show them has to widen with them or a touch-sized handle
               covers a short clip completely. */
            const handleWidth = parseFloat(style.getPropertyValue('--nle-handle-width')) || 0;
            setMetrics(prev => (
                prev.width === width && prev.labelWidth === labelWidth && prev.handleWidth === handleWidth
                    ? prev
                    : { width, labelWidth, handleWidth }
            ));
        };

        measure(element.clientWidth);
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(([entry]) => measure(entry.contentRect.width));
        observer.observe(element);
        return () => observer.disconnect();
    }, [viewportRef]);

    const { labelWidth, handleWidth } = metrics;
    const trackViewportWidth = Math.max(0, metrics.width - labelWidth);

    const secondsToPx = useCallback((seconds) => seconds * pixelsPerSecond, [pixelsPerSecond]);
    const pxToSeconds = useCallback((px) => px / pixelsPerSecond, [pixelsPerSecond]);

    /** Always leave a screen of empty track past the end, so there is somewhere
     *  to drop a clip that extends the edit. */
    const contentWidth = Math.max(
        trackViewportWidth,
        secondsToPx(totalDuration) + Math.max(200, trackViewportWidth * 0.25)
    );

    const zoomAbout = useCallback((nextPps, anchorClientX) => {
        const element = viewportRef.current;
        const clamped = Math.min(MAX_PPS, Math.max(MIN_PPS, nextPps));
        if (!element) {
            setPixelsPerSecond(clamped);
            return;
        }
        // Keep whatever is under the cursor under the cursor. Zooming about the
        // viewport's left edge instead makes the clip you are aiming at run away.
        // Scroll positions are container-relative, so the gutter comes off before
        // converting to seconds and goes back on afterwards.
        const rect = element.getBoundingClientRect();
        const offsetX = anchorClientX - rect.left;
        const secondsAtCursor = (element.scrollLeft + offsetX - labelWidth) / pixelsPerSecond;
        pendingScrollRef.current = secondsAtCursor * clamped + labelWidth - offsetX;
        setPixelsPerSecond(clamped);
    }, [pixelsPerSecond, labelWidth, viewportRef]);

    // Applied after paint: the new scroll position is only reachable once the
    // content has been laid out at the new width.
    useLayoutEffect(() => {
        const element = viewportRef.current;
        if (!element || pendingScrollRef.current === null) return;
        element.scrollLeft = Math.max(0, pendingScrollRef.current);
        pendingScrollRef.current = null;
    }, [pixelsPerSecond, viewportRef]);

    // Non-passive, because zooming must preventDefault to stop the browser
    // zooming the whole page. React's onWheel is passive and cannot.
    useEffect(() => {
        const element = viewportRef.current;
        if (!element) return undefined;

        const onWheel = (event) => {
            if (event.ctrlKey || event.metaKey) {
                event.preventDefault();
                zoomAbout(pixelsPerSecond * (event.deltaY < 0 ? 1.15 : 1 / 1.15), event.clientX);
                return;
            }
            if (event.shiftKey || Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
                event.preventDefault();
                element.scrollLeft += event.deltaX || event.deltaY;
            }
        };

        element.addEventListener('wheel', onWheel, { passive: false });
        return () => element.removeEventListener('wheel', onWheel);
    }, [pixelsPerSecond, zoomAbout, viewportRef]);

    /* Pinch to zoom.
     *
     * The viewport gives up browser pinch-zoom (`touch-action: pan-x pan-y`),
     * so the gesture has to be assembled here from raw pointers. Scale is taken
     * against the spread and scale at the moment the second finger landed, not
     * accumulated per move, for the same reason clip drags are: an accumulating
     * ratio drifts, and on a scale this one compounds.
     *
     * Surfaces that already own their gesture are excluded. A clip and the
     * ruler both set `touch-action: none` because a drag there is an edit, so a
     * second finger arriving mid-trim must not start rescaling underneath it.
     */
    useEffect(() => {
        const element = viewportRef.current;
        if (!element) return undefined;

        const points = new Map();
        let gesture = null;

        const spread = () => {
            const [a, b] = [...points.values()];
            return {
                distance: Math.hypot(a.x - b.x, a.y - b.y),
                midX: (a.x + b.x) / 2,
            };
        };

        const onDown = (event) => {
            if (event.pointerType !== 'touch') return;
            if (event.target?.closest?.('.nle-clip, .nle-ruler')) return;
            points.set(event.pointerId, { x: event.clientX, y: event.clientY });
            if (points.size === 2) {
                const { distance } = spread();
                gesture = { distance, pps: pixelsPerSecond };
            }
        };

        const onMove = (event) => {
            if (!points.has(event.pointerId)) return;
            points.set(event.pointerId, { x: event.clientX, y: event.clientY });
            if (points.size !== 2 || !gesture || gesture.distance < 1) return;
            event.preventDefault();
            const { distance, midX } = spread();
            zoomAbout(gesture.pps * (distance / gesture.distance), midX);
        };

        const onRelease = (event) => {
            points.delete(event.pointerId);
            // A gesture is over once it is no longer two fingers; the next pair
            // re-baselines rather than resuming a stale spread.
            if (points.size < 2) gesture = null;
        };

        element.addEventListener('pointerdown', onDown);
        element.addEventListener('pointermove', onMove, { passive: false });
        element.addEventListener('pointerup', onRelease);
        element.addEventListener('pointercancel', onRelease);
        return () => {
            element.removeEventListener('pointerdown', onDown);
            element.removeEventListener('pointermove', onMove);
            element.removeEventListener('pointerup', onRelease);
            element.removeEventListener('pointercancel', onRelease);
        };
    }, [pixelsPerSecond, zoomAbout, viewportRef]);

    const zoomIn = useCallback(() => setPixelsPerSecond(pps => Math.min(MAX_PPS, pps * 1.4)), []);
    const zoomOut = useCallback(() => setPixelsPerSecond(pps => Math.max(MIN_PPS, pps / 1.4)), []);

    const fitToWindow = useCallback(() => {
        if (!trackViewportWidth || totalDuration <= 0) {
            setPixelsPerSecond(DEFAULT_PPS);
            return;
        }
        pendingScrollRef.current = 0;
        setPixelsPerSecond(Math.min(MAX_PPS, Math.max(MIN_PPS, (trackViewportWidth - 24) / totalDuration)));
    }, [trackViewportWidth, totalDuration]);

    /** Keep a moving playhead on screen without fighting a manual scroll. The
     *  gutter is sticky, so anything scrolled under it is hidden — it counts as
     *  part of the left margin. */
    const revealSecond = useCallback((seconds) => {
        const element = viewportRef.current;
        if (!element) return;
        const x = labelWidth + secondsToPx(seconds);
        const { scrollLeft, clientWidth } = element;
        if (x < scrollLeft + labelWidth + 40) element.scrollLeft = Math.max(0, x - labelWidth - 40);
        else if (x > scrollLeft + clientWidth - 40) element.scrollLeft = x - clientWidth + 40;
    }, [secondsToPx, labelWidth, viewportRef]);

    /** Follow a playing playhead a page at a time. Nudging by the overshoot
     *  (as revealSecond does) pins the playhead to the right edge, so what is
     *  about to play is always just off screen; turning the page puts it back
     *  near the left with the upcoming stretch in view. A playhead that jumped
     *  behind the view -- playback restarted from zero -- is paged back to. */
    const followPlayhead = useCallback((seconds) => {
        const element = viewportRef.current;
        if (!element || !trackViewportWidth) return;
        const x = secondsToPx(seconds);
        const visibleStart = element.scrollLeft;
        const visibleEnd = visibleStart + trackViewportWidth;
        const margin = Math.min(40, trackViewportWidth * 0.1);
        if (x >= visibleStart && x <= visibleEnd - margin) return;
        element.scrollLeft = Math.max(0, x - trackViewportWidth * 0.15);
    }, [secondsToPx, trackViewportWidth, viewportRef]);

    return {
        pixelsPerSecond,
        contentWidth,
        handleWidth,
        viewportWidth: trackViewportWidth,
        secondsToPx,
        pxToSeconds,
        zoomIn,
        zoomOut,
        fitToWindow,
        revealSecond,
        followPlayhead,
    };
}
